/**
 * SectionHeader - Card section header with icon, title, and optional subtitle.
 *
 * A consistent header pattern for card sections with IconContainer.
 * Supports standard (title above subtitle) and label-first (label above title) layouts.
 *
 * @example
 * ```tsx
 * // Standard layout
 * <SectionHeader
 *   icon={Users}
 *   title="Teams"
 *   subtitle="Define teams and their simulation data"
 * />
 *
 * // Label-first layout
 * <SectionHeader
 *   icon={BarChart2}
 *   title="My Simulation"
 *   label="Simulation"
 *   layout="label-first"
 * />
 * ```
 */

import { type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { IconContainer, type IconContainerProps } from '@/components/atoms';
import { cn } from '@lean-agility/design-system/react';

export interface SectionHeaderProps {
  /** Icon component to display */
  icon: LucideIcon;
  /** Main title text */
  title: string;
  /** Subtitle text (shown below title in standard layout) */
  subtitle?: string;
  /** Label text (shown above title in label-first layout) */
  label?: string;
  /** Layout variant */
  layout?: 'standard' | 'label-first';
  /** IconContainer variant */
  iconVariant?: IconContainerProps['variant'];
  /** Optional action element (button, etc.) to show on the right */
  action?: ReactNode;
  /** Additional className for the title */
  titleClassName?: string;
}

export function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  label,
  layout = 'standard',
  iconVariant = 'accent',
  action,
  titleClassName,
}: SectionHeaderProps) {
  return (
    <div className="flex items-center gap-3">
      <IconContainer variant={iconVariant} size="md">
        <Icon className="size-5 text-text-accent" />
      </IconContainer>
      <div className="min-w-0 flex-1">
        {layout === 'label-first' && label && <p className="text-sm text-text-muted">{label}</p>}
        <h2 className={cn('text-xl font-semibold text-text-primary', titleClassName)}>{title}</h2>
        {layout === 'standard' && subtitle && <p className="text-sm text-text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
